import {
  EntityLinks,
  Goal,
  GoalGroupBy,
  GoalProgressCount,
  GoalRoadmapSortingOptions,
  GoalSortingMode,
  GroupByMode,
  ID,
  LifeArea,
  OrderDirection,
  UserSettings,
} from '@griply/types';

import { filterGoalsByType, projectOnlyTypes } from './filter-goals-by-type';
import { getGoalFolderChildren } from './get-goal-folder-children';
import { getLifeAreaGoalOrder } from './get-life-area-goal-order';
import { getProjectsOverviewCustomOrder } from './get-projects-overview-custom-order';

export type GetProjectsOverviewOrderOptions = {
  goals: Goal[];
  lifeAreas: LifeArea[];
  links?: EntityLinks;
  groupBy: GoalGroupBy;
  sortMode?: GoalSortingMode | null;
  sortDirection: OrderDirection;
  goalRoadmapSorting?: Record<GoalRoadmapSortingOptions, ID[]>;
  goalGroupSorting?: UserSettings['goalGroupSorting'];
  taskCounts?: Map<ID, number>;
  goalProgressCounts?: Map<ID, GoalProgressCount>;
};

export type ProjectsOverviewOrder = {
  // Flat order for the Goal grouping — one position per project.
  customOrder: ID[] | undefined;
  // Children per goal folder and, under Custom, per life-area section — one position per container.
  goalChildrenByParentId: Map<ID, ID[]>;
};

// The one order derivation for the Projects list on both platforms.
export const getProjectsOverviewOrder = ({
  goals,
  lifeAreas,
  links,
  groupBy,
  sortMode,
  sortDirection,
  goalRoadmapSorting,
  goalGroupSorting,
  taskCounts,
  goalProgressCounts,
}: GetProjectsOverviewOrderOptions): ProjectsOverviewOrder => {
  const goalChildren = getGoalFolderChildren(goals, {
    links,
    sortMode: sortMode ?? undefined,
    sortDirection,
    allGoals: goals,
    taskCounts,
    goalProgressCounts,
  });

  // Life-area sections rank from the same links-aware per-area order the goals overview uses.
  const goalChildrenByParentId =
    groupBy === GroupByMode.LifeArea
      ? new Map([
          ...goalChildren,
          ...getLifeAreaGoalOrder({
            goals: filterGoalsByType(goals, projectOnlyTypes),
            allGoals: goals,
            lifeAreas,
            links,
            sortMode,
            sortDirection,
            goalGroupSorting: goalGroupSorting?.lifeArea?.sorting,
            taskCounts,
            goalProgressCounts,
          }),
        ])
      : goalChildren;

  return {
    customOrder: getProjectsOverviewCustomOrder({
      goals,
      groupBy,
      sortingMode: sortMode,
      goalRoadmapSorting,
    }),
    goalChildrenByParentId,
  };
};
